import React, { useState } from "react";
import "./TaskCard.css";
import TaskDetailModal from "../TaskDetailModal/TaskDetailModal";

const TaskCard = ({ task, onUpdate, onDelete, userRole }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Add null checks for task and its properties
  if (!task) {
    return (
      <div className="task-card loading">
        <div className="task-content">
          <p>Loading task...</p>
        </div>
      </div>
    );
  }

  // Safe access to nested properties with null checks
  const assignedToId = task.assignedTo?._id;
  const currentUserId = localStorage.getItem("userId");
  const isAssignedToMe = assignedToId === currentUserId;
  const canUpdateStatus =
    isAssignedToMe || userRole === "admin" || userRole === "hr";

  // Check if user can delete the task (admin, HR, supervisor, or creator)
  const createdById = task.createdBy?._id;
  const canDelete =
    ["admin", "hr", "supervisor"].includes(userRole) ||
    createdById === currentUserId;

  const handleStatusChange = async (newStatus) => {
    if (!canUpdateStatus || !task?._id) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            completedAt: newStatus === "completed" ? new Date() : null,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        onUpdate && onUpdate(data.task);
      } else {
        console.error("Error updating task:", data.message);
      }
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete task
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (
      window.confirm(
        `Are you sure you want to delete this task?\n\n"${task.title}"\n\nThis action cannot be undone. All task data, updates, and comments will be permanently removed.`
      )
    ) {
      handleConfirmDelete();
    }
  };

  // Delete task
  const handleConfirmDelete = async () => {
    if (!canDelete || !task?._id) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        onDelete && onDelete(task._id);
      } else {
        alert(data.message || "Error deleting task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("Error deleting task");
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: "#ff4757",
      high: "#ff6b6b",
      medium: "#ffa726",
      low: "#66bb6a",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#64b5f6",
      "in-progress": "#ffb74d",
      completed: "#81c784",
      overdue: "#e57373",
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (date) => {
    if (!date) return "No deadline";

    const now = new Date();
    const taskDate = new Date(date);
    const diffTime = taskDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <span className="overdue">{Math.abs(diffDays)} days overdue</span>;
    } else if (diffDays === 0) {
      return <span className="today">Due today</span>;
    } else if (diffDays === 1) {
      return <span className="tomorrow">Due tomorrow</span>;
    } else {
      return `${diffDays} days remaining`;
    }
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      urgent: "fas fa-exclamation-circle",
      high: "fas fa-arrow-up",
      medium: "fas fa-minus",
      low: "fas fa-arrow-down",
    };
    return icons[priority] || icons.medium;
  };

  // Format time ago for updates
  const getTimeAgo = (date) => {
    if (!date) return "Unknown";

    const now = new Date();
    const updateDate = new Date(date);
    const diffTime = now - updateDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

    if (diffDays === 0) {
      if (diffHours === 0) {
        return "Recently";
      }
      return `${diffHours}h ago`;
    }
    return `${diffDays}d ago`;
  };

  // Check if task has today's update
  const hasTodaysUpdate = () => {
    if (!task.dailyUpdates || task.dailyUpdates.length === 0) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return task.dailyUpdates.some((update) => {
      const updateDate = new Date(update.date);
      updateDate.setHours(0, 0, 0, 0);
      return updateDate.getTime() === today.getTime();
    });
  };

  return (
    <>
      <div
        className={`task-card ${task.status || "pending"}`}
        onClick={() => setShowDetails(true)}
      >
        <div className="task-header">
          <div
            className="task-priority"
            style={{ color: getPriorityColor(task.priority) }}
          >
            <i className={getPriorityIcon(task.priority)}></i>
            <span>{task.priority || "medium"}</span>
          </div>
          <div className="task-actions-header">
            <div
              className="task-status"
              style={{ backgroundColor: getStatusColor(task.status) }}
            >
              {task.status || "pending"}
            </div>
            {canDelete && (
              <button
                className="delete-task-btn"
                onClick={handleDeleteClick}
                title="Delete task"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </div>

        <div className="task-content">
          <h3 className="task-title">{task.title || "Untitled Task"}</h3>
          <p className="task-description">
            {task.description
              ? `${task.description.substring(0, 100)}...`
              : "No description"}
          </p>

          <div className="task-meta">
            <div className="task-assignee">
              <i className="fas fa-user"></i>
              <span>{task.assignedTo?.name || "Unassigned"}</span>
            </div>
            <div className="task-assigner">
              <i className="fas fa-user-tie"></i>
              <span>{task.assignedBy?.name || "Unknown"}</span>
            </div>
          </div>

          {/* NEW: Update Summary Section */}
          {(task.latestDailyUpdate ||
            task.latestWeeklyUpdate ||
            task.updatesSummary) && (
            <div className="task-updates-summary">
              <div className="updates-header">
                <i className="fas fa-chart-line"></i>
                <span>Latest Activity</span>
              </div>

              {task.latestDailyUpdate && (
                <div className="update-item">
                  <div className="update-type-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="update-info">
                    <div className="update-text">
                      {task.latestDailyUpdate.workDone
                        ? `${task.latestDailyUpdate.workDone.substring(
                            0,
                            40
                          )}...`
                        : "No work details"}
                    </div>
                    <div className="update-meta">
                      <span className="update-time">
                        {getTimeAgo(task.latestDailyUpdate.date)}
                      </span>
                      <span className="update-duration">
                        {task.latestDailyUpdate.timeSpent
                          ? `${Math.round(
                              task.latestDailyUpdate.timeSpent / 60
                            )}h`
                          : "0h"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {task.updatesSummary && (
                <div className="updates-stats">
                  <div className="stat-item">
                    <i
                      className="fas fa-check-circle"
                      style={{ color: hasTodaysUpdate() ? "#4caf50" : "#ccc" }}
                    ></i>
                    <span
                      className={
                        hasTodaysUpdate() ? "updated-today" : "not-updated"
                      }
                    >
                      {hasTodaysUpdate() ? "Today's Update" : "No Update Today"}
                    </span>
                  </div>
                  {task.updatesSummary.totalDailyUpdates > 0 && (
                    <div className="stat-item">
                      <span className="stat-number">
                        {task.updatesSummary.totalDailyUpdates}
                      </span>
                      <span className="stat-label">Daily Updates</span>
                    </div>
                  )}
                  {task.updatesSummary.totalTimeSpent > 0 && (
                    <div className="stat-item">
                      <span className="stat-number">
                        {task.updatesSummary.totalTimeSpent}h
                      </span>
                      <span className="stat-label">Total Time</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="task-footer">
            <div className="task-deadline">
              <i className="fas fa-calendar"></i>
              {formatDate(task.deadline)}
            </div>
            <div className="task-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${task.progress || 0}%` }}
                ></div>
              </div>
              <span>{task.progress || 0}%</span>
            </div>
          </div>

          {canUpdateStatus && task.status !== "completed" && (
            <div className="task-actions">
              <div className="status-buttons">
                {task.status === "pending" && (
                  <button
                    className="status-btn start-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange("in-progress");
                    }}
                    disabled={updating}
                  >
                    <i className="fas fa-play"></i>
                    {updating ? "Starting..." : "Start"}
                  </button>
                )}
                {task.status === "in-progress" && (
                  <button
                    className="status-btn complete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange("completed");
                    }}
                    disabled={updating}
                  >
                    <i className="fas fa-check"></i>
                    {updating ? "Completing..." : "Complete"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {task.comments && task.comments.length > 0 && (
          <div className="task-comments-info">
            <i className="fas fa-comments"></i>
            <span>{task.comments.length} comments</span>
          </div>
        )}
      </div>

      {showDetails && (
        <TaskDetailModal
          task={task}
          onClose={() => setShowDetails(false)}
          onUpdate={onUpdate}
          onDelete={onDelete}
          userRole={userRole}
        />
      )}
    </>
  );
};

export default TaskCard;
